import axios from "axios";
import { persistor } from "../../redux";
import {
  AUTH_TOKEN_KEY,
  secureGetItem,
  secureSetItem,
  secureRemoveItem,
} from "../../utils/secureStorage";
import { markFormClean } from "../Joinee/UnsavedChanges";

const AUTH_LOGOUT_MESSAGE_KEY = "authLogoutMessage";
const PUBLIC_AUTH_PATH = /\/auth\/(login|verifyOTP|verifyPassword|resendOTP)(\/|\?|$)/;

export const getAuthToken = () => {
  try {
    const token = secureGetItem(AUTH_TOKEN_KEY);
    return token == null ? null : String(token);
  } catch (e) {
    return null;
  }
};

export const setAuthToken = (token) => {
  try {
    secureSetItem(AUTH_TOKEN_KEY, token);
  } catch (e) {
    console.error("Unable to set auth token", e);
  }
};

export const removeAuthToken = () => {
  try {
    secureRemoveItem(AUTH_TOKEN_KEY);
  } catch (e) {
    console.error("Unable to remove auth token", e);
  }
};

let sessionResetInProgress = false;

const resetSessionAndRedirect = (message) => {
  if (sessionResetInProgress) {
    return;
  }
  sessionResetInProgress = true;
  try {
    sessionStorage.setItem(AUTH_LOGOUT_MESSAGE_KEY, message);
  } catch (e) {
    console.error("Unable to set logout message", e);
  }
  removeAuthToken();
  const redirectToLogin = function () {
    window.location.replace("/login");
  };
  persistor.purge().then(redirectToLogin, redirectToLogin);
};

/** Logout: revoke token on server, clear secure storage + redux. */
export const performLogout = function () {
  const finish = function () {
    removeAuthToken();
    return persistor.purge();
  };

  return LoginAPI.logout()
    .then(finish, finish)
    .catch(finish);
};

export const consumeAuthLogoutMessage = () => {
  try {
    const message = sessionStorage.getItem(AUTH_LOGOUT_MESSAGE_KEY);
    if (message) {
      sessionStorage.removeItem(AUTH_LOGOUT_MESSAGE_KEY);
      return message;
    }
  } catch (e) {
    console.error("Unable to read logout message", e);
  }
  return null;
};

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE}/`,
  timeout: 15000,
});

instance.interceptors.response.use(
  function (response) {
    const method = String((response.config && response.config.method) || "get").toLowerCase();
    const body = response.data;
    if (method !== "get" && !(body && body.success === false)) {
      markFormClean();
    }
    return response;
  },
  function (error) {
    if (error.response) {
      const status = error.response.status;
      const requestUrl = (error.config && error.config.url) || "";
      const isPublicAuthRequest = PUBLIC_AUTH_PATH.test(requestUrl);

      if (status === 401 && !isPublicAuthRequest) {
        const responseData = error.response.data || {};
        const code = responseData.code;
        const message =
          code === "USER_INACTIVE"
            ? responseData.message ||
              "Your account has been blocked. Please contact admin."
            : "Session expired. Please login again.";
        resetSessionAndRedirect(message);
      }
    }
    return Promise.reject(error);
  }
);

/* export class ApiUtils {
  instance: AxiosInstance
  constructor() {
    this.instance = axios.create({
      baseURL: "https://api.example.com",
    })
  }
  async get(url: string, params?: any) {
    const response = await this.instance.get(url, {
      params: params,
    })
    return response.data
  }
}*/

const responseBody = (response) => response.data;

const requests = {
  get: (url) => instance.get(url).then(responseBody),
  getAuth: (url, body, params) =>
    instance
      .get(url, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        params: params,
      })
      .then(responseBody),
  getAuthBlob: (url, body, params) =>
    instance.get(url, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
      params: params,
      responseType: "blob",
    }),
  post: (url, body) =>
    instance
      .post(url, body, { headers: { "content-type": "application/json" } })
      .then(responseBody),
  postAuth: (url, body) =>
    instance
      .post(url, body, {
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
      .then(responseBody),
  putAuth: (url, body) =>
    instance
      .put(url, body, {
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
      .then(responseBody),
  patchAuth: (url, body) =>
    instance
      .patch(url, body, {
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
      .then(responseBody),
  deleteAuth: (url) =>
    instance
      .delete(url, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
      .then(responseBody),
  deleteAuthWithParams: (url, params) =>
    instance
      .delete(url, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        params: params,
      })
      .then(responseBody),
};

export const LoginAPI = {
  role: (data) => requests.post("/api/role", data),
  login: (data) => requests.post("/auth/login", data),
  verifyOTP: (data) => requests.post("/auth/verifyOTP", data),
  verifyPassword: (data) => requests.post("/auth/verifyPassword", data),
  resendOTP: (data) => requests.post("/auth/resendOTP", data),
  logout: () => requests.postAuth("/auth/logout", {}),
};

export const UserAPI = {
  me: () => requests.getAuth("/user/me"),
  getUser: (id) => requests.getAuth(`/user/${id}`),
  createUser: (data) => requests.postAuth("/user/create", data),
  deleteUser: (id) => requests.deleteAuth(`/user/${id}`),
  updateUser: (id, data) => requests.postAuth(`/user/${id}`, data),
  updateProfilePhoto: (id, data) =>
    requests.postAuth(`/user/${id}/profile-photo`, data),
  updateGettingStarted: (id) => requests.postAuth(`/user/gettingStarted/${id}`),
  newJoineeStatus: (id) => requests.getAuth(`/user/newjoineestatus/${id}`),
};

export const NewJoineeAPI = {
  getEmploymentHistory: (id) =>
    requests.getAuth(`/newJoinee/employmentHistory/${id}`),
  createEmploymentHistory: (data) =>
    requests.postAuth(`/newJoinee/employmentHistory/new`, data),
  updateEmploymenthistory: (id, data) =>
    requests.putAuth(`/newJoinee/employmentHistory/${id}`, data),
  deleteEmploymentHistory: (id) =>
    requests.deleteAuth(`/newJoinee/employmentHistory/${id}`),
};

export const BankDetailsAPI = {
  getBankDetails: (id) => requests.getAuth(`/newJoinee/bankDetails/${id}`),
  createBankDetails: (data) =>
    requests.postAuth(`/newJoinee/bankDetails/new`, data),
  updateBankDetails: (id, data) =>
    requests.putAuth(`/newJoinee/bankDetails/${id}`, data),
};

export const MedicalAPI = {
  getMedicalInsurance: (id) => requests.getAuth(`/newJoinee/medical/${id}`),
  createMedicalInsurance: (data) =>
    requests.postAuth(`/newJoinee/medical/new`, data),
  updateMedicalInsurance: (id, data) =>
    requests.putAuth(`/newJoinee/medical/${id}`, data),
  deleteMedicalInsurance: (id) =>
    requests.deleteAuth(`/newJoinee/medical/${id}`),
};

export const EducationAPI = {
  getQualifications: (id) => requests.getAuth(`/newJoinee/qualification/${id}`),
  createQualifications: (data) =>
    requests.postAuth(`/newJoinee/qualification/new`, data),
  updateQualifications: (id, data) =>
    requests.putAuth(`/newJoinee/qualification/${id}`, data),
  deleteQualifications: (id) =>
    requests.deleteAuth(`/newJoinee/qualification/${id}`),
};

export const SelfDeclarationAPI = {
  getSelfDeclaration: (id) => requests.getAuth(`/sdf/selfdeclaration/${id}`),
  createSelfDeclaration: (id, data) =>
    requests.postAuth(`/sdf/selfdeclaration/${id}`, data),
  updateSelfDeclaration: (id, data) =>
    requests.putAuth(`/sdf/selfdeclaration/${id}`, data),
  deleteSelfDeclaration: (id) =>
    requests.deleteAuth(`/sdf/selfdeclaration/${id}`),
  selfDeclarationStatus: (id) =>
    requests.getAuth(`/sdf/selfdeclarationstatus/${id}`),
};

export const EpfForm = {
  getEpfForm: (id) => requests.getAuth(`/epf/epfform/${id}`),
  createEpfForm: (id, data) => requests.postAuth(`/epf/epfform/${id}`, data),
  updateEpfForm: (id, data) => requests.putAuth(`/epf/epfform/${id}`, data),
  deleteEpfForm: (id) => requests.deleteAuth(`/epf/epfform/${id}`),
  epfFormStatus: (id) => requests.getAuth(`/epf/epfformstatus/${id}`),
};

export const Form11API = {
  getform11: (id) => requests.getAuth(`/form11/${id}`),
  createform11: (id, data) => requests.postAuth(`/form11/${id}`, data),
  updateform11: (id, data) => requests.putAuth(`/form11/${id}`, data),
  deleteform11: (id) => requests.deleteAuth(`/form11/${id}`),
  form11Status: (id) => requests.getAuth(`/form11/form11status/${id}`),
};

export const FormfAPI = {
  getformf: (id) => requests.getAuth(`/formf/${id}`),
  createformf: (id, data) => requests.postAuth(`/formf/${id}`, data),
  updateformf: (id, data) => requests.putAuth(`/formf/${id}`, data),
  deleteformf: (id) => requests.deleteAuth(`/formf/${id}`),
  formfStatus: (id) => requests.getAuth(`/formf/formfstatus/${id}`),
};

export const FormQAPI = {
  getformq: (id) => requests.getAuth(`/formq/${id}`),
  createformq: (id, data) => requests.postAuth(`/formq/${id}`, data),
  updateformq: (id, data) => requests.putAuth(`/formq/${id}`, data),
  deleteformq: (id) => requests.deleteAuth(`/formq/${id}`),
  formqStatus: (id) => requests.getAuth(`/formq/formqstatus/${id}`),
};

export const NdaAPI = {
  getNda: (id) => requests.getAuth(`/nda/${id}`),
  createNda: (id, data) => requests.postAuth(`/nda/${id}`, data),
  updateNda: (id, data) => requests.putAuth(`/nda/${id}`, data),
  deleteNda: (id) => requests.deleteAuth(`/nda/${id}`),
  NdaStatus: (id) => requests.getAuth(`/nda/ndastatus/${id}`),
};

export const AdminAPI = {
  createNewJoinee: (data) => requests.postAuth(`/hr/create`, data),
  getListNewJoinee: (searchKey = undefined) => {
    const params = new URLSearchParams();
    if (searchKey) params.append("name", searchKey);
    return requests.getAuth(`/hr/all`, null, params);
  },
  updateNewJoinee: (id, data) => requests.postAuth(`/hr/${id}`, data),
  getNewJoinee: (id) => requests.getAuth(`/hr/${id}`),
  activeInactiveNewJoinee: (id, isActive) =>
    requests.putAuth(`/hr/status/${id}/${isActive}`),
};

export const HrStatusAPI = {
  hrFormStatus: (id, status, formname, comment) =>
    requests.postAuth(`hr/statuschange/${id}/${status}/${formname}`, comment),
};

export const ActivityAPI = {
  list: (id, params) => requests.getAuth(`/hr/activity/${id}`, null, params),
};

export const HrMailAPI = {
  gethrMail: (id) => requests.getAuth(`/hr/reports/sendmail/${id}`),
};

export const SuperAdminAPI = {
  createHR: (data) => requests.postAuth(`/superAdmin/create`, data),
  getListAllHR: (searchKey = undefined) => {
    const params = new URLSearchParams();
    if (searchKey) params.append("name", searchKey);
    return requests.getAuth(`/superAdmin/all`, null, params);
  },
  updateHR: (id, data) => requests.postAuth(`/superAdmin/${id}`, data),
  getHR: (id) => requests.getAuth(`/superAdmin/${id}`),
  activeInactiveHR: (id, isActive) =>
    requests.putAuth(`/superAdmin/status/${id}/${isActive}`),
};

export const ProfileAPI = {
  getUserProfile: (id) => requests.getAuth(`/user/profile/${id}`),
  getResetPassword: (id) =>
    requests.putAuth(`/user/profile/resetPassword/${id}`),
};

export const TotalNewJoinee = {
  getTotalNewJoinee: (id) => requests.getAuth(`/hr/dashboard/newjoinee-list`),
};

export const TotalHR = {
  getTotalHR: (id) => requests.getAuth(`/superAdmin/dashboard/hr-list`),
};

export const DownloadFormHtmlToPdf = {
  getform: (id, AllHtmlData) =>
    requests.getAuth(`/hr/downloadform/${id}/${AllHtmlData}`),
};

export const DownloadExcelsheet = {
  getExcelsheet: (id) =>
    requests.getAuthBlob(`/hr/newJoinerExcelFileDownload/${id}`),
};

export const DraftAPI = {
  saveDraft: ({ formType, section, data }) =>
    requests.postAuth("/api/drafts", { formType, section, data }),
  getLatestDraft: ({ formType, section }) =>
    requests.getAuth("/api/drafts", null, { formType, section }),
  deleteDraft: ({ formType, section }) =>
    requests.deleteAuthWithParams("/api/drafts", { formType, section }),
  getExcelsheet: (id) =>
    requests.getAuthBlob(`/hr/newJoinerExcelFileDownload/${id}`),
};
